import { deviceLocal } from '@/privacy/deviceStorage';
import {
  isWaitingStudyId,
  pickWaitingStudy,
  waitingStudy,
  type IWaitingStudy,
  type WaitingStudyId,
} from '@featit/shared/brand/waiting-motion';
import { shellWaitingHandOver } from '@/shell/shellDetection';

/**
 * F-0578 (`docs/concept-waiting-motion.md`, WAIT-3 to WAIT-5): the one study this launch draws.
 *
 * A launch is a page load. The first wait to draw picks; every wait after it in the same page draws
 * the same study from the same start time, so two waits on one screen move in step and never show
 * two studies at once. The pick is remembered on the device, so the next launch draws another.
 * A surface that drew first (the iPhone shell, a desktop window) hands its choice over with
 * `adoptWaitingStudy` before anything draws.
 */

/** The last launch's pick, on this device. */
export const LAST_STUDY_KEY = 'featit:waiting:last';
/**
 * WAIT-5: the person's *Waiting animation* setting, cached on the device so a launch reads it
 * without a request. A study id fixes it; anything else (or nothing) is Random.
 */
export const FIXED_STUDY_KEY = 'featit:waiting:study';

interface ISessionStudy {
  study: IWaitingStudy;
  /** `performance.now()` at the session's first wait: every wait's clock starts here. */
  startedAt: number;
}

let session: ISessionStudy | null = null;

function read(key: string): string | null {
  try {
    return deviceLocal.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    deviceLocal.setItem(key, value);
  } catch {
    // Storage unavailable: the next launch picks from all ten (WAIT-3).
  }
}

/** The session `adoptWaitingStudy` has just made (a function, so the type is the variable's own). */
function adopted(): ISessionStudy {
  return session as ISessionStudy;
}

export function sessionWaitingStudy(): ISessionStudy {
  if (session) return session;
  // WAIT-4 (T-0667): inside the iPhone and iPad app the shell drew first, and its study and clock
  // come with the descriptor. A wait never starts its own pick over the shell's.
  const shell = shellWaitingHandOver();
  if (shell && adoptWaitingStudy(shell.study, Math.max(0, Date.now() - shell.startedAtMs))) {
    return adopted();
  }
  const id = pickWaitingStudy({ last: read(LAST_STUDY_KEY), fixed: read(FIXED_STUDY_KEY) });
  write(LAST_STUDY_KEY, id);
  session = { study: waitingStudy(id), startedAt: performance.now() };
  return session;
}

/**
 * WAIT-4: take the study a surface that drew earlier in this launch chose, and its clock, so the
 * hand-over shows no change. `elapsedMs` is how long that surface has been drawing it.
 */
export function adoptWaitingStudy(id: string, elapsedMs = 0): boolean {
  if (!isWaitingStudyId(id)) return false;
  write(LAST_STUDY_KEY, id);
  session = { study: waitingStudy(id as WaitingStudyId), startedAt: performance.now() - elapsedMs };
  return true;
}

/** Tests only: forget this launch's pick. */
export function resetWaitingSessionForTests(): void {
  session = null;
}
